import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import styles from "./MapDrawer.module.css";

/** Imperative handle exposed via ref. Used by OverviewPage to trigger the
 *  reverse animation when the user deselects via a map marker click — same
 *  animation path the pull-to-collapse gesture uses, so deselect and pull
 *  share visual treatment. */
export interface MapDrawerHandle {
  /** Play the reverse animation, then fire `onCollapse` for the parent to
   *  clear the state that put us in constrained mode. Safe to call when
   *  the drawer isn't in constrained mode (no-op). */
  collapse: () => void;
}

interface MapDrawerProps {
  /** Optional footer pinned to the bottom of the drawer column. */
  footer?: ReactNode | undefined;
  /**
   * sessionStorage key — if set to "1" when the drawer mounts, plays a
   * slide-up animation and clears the flag. Used to make a cross-page
   * map → drawer transition read as continuous.
   */
  animateInFlag?: string | undefined;
  /** Class on the drawer root — for parent layout (xl flex slot, etc). */
  className?: string | undefined;
  /**
   * When set, the drawer locks at `nav-bar-height + mapRevealHeight` from
   * viewport top once scrolled fully up — leaving this much of the map
   * visible above the drawer so the user can keep browsing the map. The
   * list inside the drawer scrolls past that point. Pass a CSS length
   * (e.g. "240px"). When undefined (default), the drawer covers the map
   * fully when scrolled up.
   *
   * No effect at xl, where the drawer flattens into a column anyway.
   */
  mapRevealHeight?: string | undefined;
  /**
   * Called when the user pulls the drawer down past its top in constrained
   * mode (touch drag-down at scrollTop=0 past a threshold, or scroll-wheel
   * up past zero). The drawer plays a collapse animation first; this fires
   * at the end so the consumer can clear whatever was driving the
   * constrained mode (e.g. set selectedSpotId to null) and the drawer
   * reverts to its default flow position.
   *
   * Only fires when `mapRevealHeight` is set.
   */
  onCollapse?: (() => void) | undefined;
  children: ReactNode;
}

/** Duration of the rise/collapse keyframe animations (see MapDrawer.module.css).
 *  Kept in JS so the pull-to-collapse handler can schedule onCollapse for the
 *  moment the reverse animation finishes. */
const COLLAPSE_ANIMATION_MS = 300;

/** Pixels of finger drag-down (from touch-start) before pull-to-collapse
 *  triggers. Tuned to feel intentional but not too sticky on mobile. */
const PULL_THRESHOLD_PX = 60;

/** Wheel deltaY threshold for desktop pull-to-collapse (negative = up). */
const WHEEL_THRESHOLD_PX = 50;

export const MapDrawer = forwardRef<MapDrawerHandle, MapDrawerProps>(function MapDrawer(
  {
    footer,
    animateInFlag,
    className,
    mapRevealHeight,
    onCollapse,
    children,
  },
  ref,
) {
  const [animateIn, setAnimateIn] = useState(false);
  const [collapsing, setCollapsing] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);

  // Expose collapse() so the parent can drive the reverse animation when a
  // marker click deselects (rather than instantly clearing the state and
  // snapping the drawer back to its peek position).
  useImperativeHandle(
    ref,
    () => ({
      collapse: () => {
        if (mapRevealHeight) setCollapsing(true);
      },
    }),
    [mapRevealHeight],
  );
  // Hold the latest onCollapse in a ref so the touch/wheel effect below
  // doesn't have to depend on it — consumers commonly pass a fresh arrow
  // every render, and re-attaching listeners + cancelling the in-flight
  // collapse timer on each parent re-render is what previously stranded the
  // drawer in `.collapsing` state mid-animation.
  const onCollapseRef = useRef(onCollapse);
  useEffect(() => {
    onCollapseRef.current = onCollapse;
  });

  useEffect(() => {
    if (!animateInFlag) return;
    if (sessionStorage.getItem(animateInFlag) === "1") {
      sessionStorage.removeItem(animateInFlag);
      setAnimateIn(true);
    }
  }, [animateInFlag]);

  // Collapse animation timer. When `collapsing` flips on, schedule the
  // reverse-animation duration; after it fires, clear the local state and
  // notify the parent so it can release the constrained mode. Owning the
  // timer in its own effect (keyed only on `collapsing`) means parent
  // re-renders during the animation can't cancel it.
  useEffect(() => {
    if (!collapsing) return;
    const t = window.setTimeout(() => {
      setCollapsing(false);
      onCollapseRef.current?.();
    }, COLLAPSE_ANIMATION_MS);
    return () => window.clearTimeout(t);
  }, [collapsing]);

  // Safety: if the drawer exits constrained mode externally (e.g. user
  // toggled the same marker before the pull-collapse gesture could finish),
  // clear any lingering `.collapsing` class so a later re-entry to the
  // constrained mode plays the forward animation, not the cached reverse.
  useEffect(() => {
    if (!mapRevealHeight && collapsing) setCollapsing(false);
  }, [mapRevealHeight, collapsing]);

  // Pull-to-collapse touch + wheel handlers. Only attached while in
  // constrained mode. Triggers `setCollapsing(true)`; the timer effect
  // above handles the rest.
  useEffect(() => {
    const drawer = drawerRef.current;
    if (!drawer || !mapRevealHeight) return;

    let touchStartY: number | null = null;
    let touchStartScrollTop = 0;

    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? null;
      touchStartScrollTop = drawer.scrollTop;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (touchStartY === null) return;
      if (touchStartScrollTop > 0 || drawer.scrollTop > 0) return;
      const delta = (e.touches[0]?.clientY ?? 0) - touchStartY;
      if (delta > PULL_THRESHOLD_PX) {
        touchStartY = null;
        setCollapsing(true);
      }
    };
    const onTouchEnd = () => {
      touchStartY = null;
    };
    const onWheel = (e: WheelEvent) => {
      if (drawer.scrollTop === 0 && e.deltaY < -WHEEL_THRESHOLD_PX) {
        setCollapsing(true);
      }
    };

    drawer.addEventListener("touchstart", onTouchStart, { passive: true });
    drawer.addEventListener("touchmove", onTouchMove, { passive: true });
    drawer.addEventListener("touchend", onTouchEnd, { passive: true });
    drawer.addEventListener("touchcancel", onTouchEnd, { passive: true });
    drawer.addEventListener("wheel", onWheel, { passive: true });

    return () => {
      drawer.removeEventListener("touchstart", onTouchStart);
      drawer.removeEventListener("touchmove", onTouchMove);
      drawer.removeEventListener("touchend", onTouchEnd);
      drawer.removeEventListener("touchcancel", onTouchEnd);
      drawer.removeEventListener("wheel", onWheel);
    };
  }, [mapRevealHeight]);

  const classes = [
    styles.drawer,
    animateIn && styles.animateIn,
    collapsing && styles.collapsing,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={drawerRef}
      className={classes}
      data-map-reveal={mapRevealHeight ? "" : undefined}
      style={
        mapRevealHeight !== undefined
          ? ({ "--map-reveal": mapRevealHeight } as CSSProperties)
          : undefined
      }
    >
      <div className={styles.handle} aria-hidden />
      <div className={styles.body}>{children}</div>
      {footer !== undefined && <div className={styles.footer}>{footer}</div>}
    </div>
  );
});
